import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, retry, switchMap } from 'rxjs';
import { AppConfigService, Page } from '@markant-platform/shared/data-access';
import { PriceRequestFormData } from './model/price-request-form-data';
import {
  BackofficeBookingData,
  BackofficeCartridge,
  BackofficeCartridgeGroups,
  BackofficeCartridgeUpdateVariant,
  BackofficeWidget,
  BookingCartridgeData,
  BookingEzrDraft,
  PriceRequestOrganizationAdminData,
  PriceRequestServiceAdminData,
  PriceRequestSortField,
  PriceRequestSortOrder,
  PriceRequestStates,
  PriceRequestTransaction,
} from '@markant-platform/shared/backoffice/model';
import { BookingData, Widget } from '@markant-platform/cartridge/model';
import { CartridgeDto } from '@markant-platform/shared/cartridge/model';
import { SortAttributes } from '@markant-platform/shared/table/model';
import { OrganizationType } from 'data-model';
import { createRetryConfig } from '@markant-platform/shared/util-helpers';

@Injectable({
  providedIn: 'root',
})
export class BackofficeCartridgeService {
  constructor(private readonly http: HttpClient) {}

  private readonly cartridgeServiceApiUrl: string = AppConfigService.appConfiguration.apiUrl['leistungService'];
  private readonly bookingServiceUrl = AppConfigService.appConfiguration.apiUrl['bookingService'];
  private readonly priceRequestServiceUrl = AppConfigService.appConfiguration.apiUrl['priceRequestService'];

  /**
   * This function returns all stored cartridges if user is platform admin or all variants the currently logged-in User is Leistung Administrator for
   * @return {Observable<CartridgeDto[]>} - cartridge list
   */
  getAllCartridgeVariants(
    organizationType?: OrganizationType,
    isEzr?: boolean,
    showDemos?: boolean
  ): Observable<CartridgeDto[]> {
    const projectionUrl = `${this.cartridgeServiceApiUrl}/projection`;

    return this.http.get<CartridgeDto[]>(projectionUrl, {
      params: new HttpParams({
        fromObject: {
          ...(organizationType && isEzr && { organizationType, isEzr }),
          ...(showDemos && { showDemos }),
        },
      }),
    });
  }

  /**
   * This function returns all stored cartridges if user is platform admin or all variants included with demo variants the currently logged-in User is Leistung Administrator for
   * @return {Observable<CartridgeDto[]>} - cartridge list
   */
  getAllCartridgeVariantsWithDemo(organizationType?: OrganizationType, isEzr?: boolean): Observable<CartridgeDto[]> {
    const projectionUrl = `${this.cartridgeServiceApiUrl}/projection`;

    return this.http.get<CartridgeDto[]>(
      projectionUrl,
      organizationType && isEzr
        ? { params: new HttpParams({ fromObject: { organizationType, isEzr: isEzr.toString(), showDemos: 'true' } }) }
        : { params: new HttpParams({ fromObject: { showDemos: 'true' } }) }
    );
  }

  /**
   * Fetches a cartridge object from backend
   *
   * @param {string} id - ID of the cartridge to fetch
   * @returns {Observable<CartridgeDto>} - cartridge object
   */
  getCartridgeVariantById(id: string): Observable<CartridgeDto> {
    return this.http.get<CartridgeDto>(`${this.cartridgeServiceApiUrl}/projection/${id}`);
  }

  /**
   * Fetches a detailed cartridge object for service management menu
   *
   * @param {string} id - ID of the cartridge
   * @returns {Observable<BackofficeCartridge>} - backoffice cartridge object
   */
  getCartridgeDetailsById(id: string): Observable<BackofficeCartridge> {
    return this.http.get<BackofficeCartridge>(`${this.cartridgeServiceApiUrl}/${id}`).pipe(
      map(cartridge => ({
        ...cartridge,
        variants: cartridge.variants.map(variant => ({
          ...variant,
          widgets: variant.widgets?.map(widget => this.mapWidgetToBackofficeWidget(widget as unknown as Widget)),
        })),
      }))
    );
  }

  /**
   * Get booked data for current organization.
   * By using status argument, it is possible to specify the booking states that you want to receive.
   *
   * @param index page offset to get the chunk for
   * @param size number of items returned
   * @param search search string for search params (optional)
   * @param filter filter string to append filter option to query (optional)
   * @param sort sort field and order (optional)
   *
   * @returns {Observable<Page<BackofficeBookingData>>} - paginated result set of all booked cartridges with booking metadata
   */
  getBookingsForOrganization(
    index: number,
    size: number,
    search = '',
    filter = '',
    sort: SortAttributes | undefined = undefined
  ): Observable<Page<BackofficeBookingData>> {
    const searchParam: string = !search ? '' : `&search=${encodeURIComponent(search)}`;
    const filterParam: string = !filter ? '' : `&${filter}`;
    const sortParams: string = !sort ? '' : `&sortField=${sort.sortField}&sortOrder=${sort.sortOrder}`;
    return this.http.get<Page<BackofficeBookingData>>(
      `${this.bookingServiceUrl}?index=${index}&size=${size}${searchParam}${filterParam}${sortParams}`
    );
  }

  getUnbookedCartridgesForOrganization(organizationId: string): Observable<BookingCartridgeData[]> {
    return this.http.get<BookingCartridgeData[]>(`${this.bookingServiceUrl}/unbooked/${organizationId}`);
  }

  orderSelectedCartridges(bookingEzrDraft: BookingEzrDraft): Observable<BookingData[]> {
    return this.http
      .post<BookingData[]>(`${this.bookingServiceUrl}/ezr`, bookingEzrDraft)
      .pipe(retry(createRetryConfig(3, ['CRT-070'])));
  }

  getPriceRequests<PriceRequestData extends PriceRequestOrganizationAdminData | PriceRequestServiceAdminData>(
    pageIndex: number,
    pageSize: number,
    activeFilter: Record<string, string[]>,
    searchQuery: string,
    sortAttributes?: SortAttributes
  ): Observable<Page<PriceRequestData>> {
    const statesParam = (activeFilter['states']?.length ? activeFilter['states'] : PriceRequestStates)
      .map(state => '&states=' + state)
      .join('');
    const cartridgesParam = (activeFilter['services']?.length ? activeFilter['services'] : [])
      .map(state => '&services=' + state)
      .join('');
    const variantsParam = (activeFilter['variants']?.length ? activeFilter['variants'] : [])
      .map(state => '&variants=' + state)
      .join('');
    const typesParam = (activeFilter['types']?.length ? activeFilter['types'] : [])
      .map(state => '&types=' + state)
      .join('');
    const searchQueryParam = searchQuery ? `&query=${encodeURIComponent(searchQuery)}` : '';
    const sortFieldparam = sortAttributes?.sortField
      ? `&sortField=${PriceRequestSortField[sortAttributes.sortField as keyof typeof PriceRequestSortField]}`
      : '';
    const sortOrderparam = sortAttributes?.sortOrder
      ? `&sortOrder=${PriceRequestSortOrder[sortAttributes.sortOrder as keyof typeof PriceRequestSortOrder]}`
      : '';
    return this.http.get<Page<PriceRequestData>>(
      `${this.priceRequestServiceUrl}?index=${pageIndex}&size=${pageSize}${cartridgesParam}${variantsParam}${statesParam}${typesParam}${searchQueryParam}${sortFieldparam}${sortOrderparam}`
    );
  }

  /**
   * This function sends PUT request to move the price request state: OPEN -> IN_PROGRESS
   *
   * @param priceRequestId - id of the price request
   * @param priceRequestFormData - user input data
   * @param version - version of the price request
   */
  updateOffer(
    priceRequestId: string,
    priceRequestFormData: PriceRequestFormData | undefined,
    version: number
  ): Observable<PriceRequestServiceAdminData> {
    const requestBody = {
      name: PriceRequestTransaction.CreateUpdateOfferDraft,
      offerParameter: priceRequestFormData?.offerParameter,
      offerPrice: priceRequestFormData?.offerPrice
        ? {
            centAmount: Math.round(priceRequestFormData.offerPrice * 100),
            currency: 'EUR',
          }
        : null,
      validUntil: priceRequestFormData?.offerValidUntil
        ? new Date(priceRequestFormData.offerValidUntil.split('.').reverse().join('-')).toISOString()
        : undefined,
      version,
    };

    return this.http.put<PriceRequestServiceAdminData>(`${this.priceRequestServiceUrl}/${priceRequestId}`, requestBody);
  }

  /**
   * This function sends two PUT request to move the price request state: OPEN|IN_PROGRESS -> IN_PROGRESS -> OFFER_SENT
   *
   * @param priceRequestId - id of the price request
   * @param priceRequestFormData - user input data
   * @param version - version of the price request
   */
  sendOffer(
    priceRequestId: string,
    priceRequestFormData: PriceRequestFormData,
    version: number
  ): Observable<PriceRequestServiceAdminData> {
    const requestBody = {
      name: PriceRequestTransaction.SendOffer,
      offerParameter: priceRequestFormData.offerParameter,
      offerPrice: {
        centAmount: Math.round(priceRequestFormData.offerPrice * 100),
        currency: 'EUR',
      },
      validUntil: new Date(priceRequestFormData.offerValidUntil.split('.').reverse().join('-')).toISOString(),
      version,
    };

    return this.http
      .put<PriceRequestServiceAdminData>(`${this.priceRequestServiceUrl}/${priceRequestId}`, {
        ...requestBody,
        name: PriceRequestTransaction.CreateUpdateOfferDraft,
      })
      .pipe(
        switchMap(updatedPriceRequest =>
          this.http.put<PriceRequestServiceAdminData>(`${this.priceRequestServiceUrl}/${priceRequestId}`, {
            ...requestBody,
            version: updatedPriceRequest.version,
          })
        )
      );
  }

  rejectOffer(priceRequestId: string, version: number, reason?: string): Observable<PriceRequestOrganizationAdminData> {
    const requestBody = {
      name: PriceRequestTransaction.RejectOffer,
      reason,
      version,
    };

    return this.http.put<PriceRequestOrganizationAdminData>(
      `${this.priceRequestServiceUrl}/${priceRequestId}`,
      requestBody
    );
  }

  rejectPriceRequest(
    priceRequestId: string,
    reason: string,
    version: number
  ): Observable<PriceRequestServiceAdminData> {
    const requestBody = {
      name: PriceRequestTransaction.RejectPriceRequest,
      reason,
      version,
    };

    return this.http.put<PriceRequestServiceAdminData>(`${this.priceRequestServiceUrl}/${priceRequestId}`, requestBody);
  }

  retractPriceRequest(
    priceRequestId: string,
    version: number,
    reason?: string
  ): Observable<PriceRequestOrganizationAdminData> {
    const requestBody = {
      name: PriceRequestTransaction.RetractPriceRequest,
      reason,
      version,
    };

    return this.http.put<PriceRequestOrganizationAdminData>(
      `${this.priceRequestServiceUrl}/${priceRequestId}`,
      requestBody
    );
  }

  getPriceRequestById(priceRequestId: string): Observable<PriceRequestServiceAdminData> {
    return this.http.get<PriceRequestServiceAdminData>(`${this.priceRequestServiceUrl}/${priceRequestId}`);
  }

  getPriceRequestForOrganizationById(priceRequestId: string): Observable<PriceRequestOrganizationAdminData> {
    return this.http.get<PriceRequestOrganizationAdminData>(`${this.priceRequestServiceUrl}/${priceRequestId}`);
  }

  getOfferDocumentPreviewAsUrl(priceRequestId: string): Observable<string> {
    return this.http
      .get<string>(`${this.priceRequestServiceUrl}/${priceRequestId}/offer`, {
        responseType: 'text' as 'json',
      })
      .pipe(map(arrayBuffer => URL.createObjectURL(new Blob([arrayBuffer], { type: 'text/html;charset=UTF-8' }))));
  }

  addComment(
    newComment: string,
    priceRequestId: string,
    priceRequestVersion: number
  ): Observable<PriceRequestServiceAdminData> {
    return this.http.put<PriceRequestServiceAdminData>(`${this.priceRequestServiceUrl}/${priceRequestId}`, {
      name: PriceRequestTransaction.AddComment,
      commentText: newComment,
      version: priceRequestVersion,
    });
  }

  private mapWidgetToBackofficeWidget(widget: Widget): BackofficeWidget {
    return {
      type: widget.externalLink ? 'EXTERNAL_LINK' : 'FOCUS_VIEW',
      id: widget.id,
      displayName: widget.displayName,
    };
  }

  /**
   * Get a list of all service groups and respective cartridges for admin overview.
   * @return {Observable<CartridgeDto[]>} - cartridge groups list
   */
  getCartridgeGroupsAndServices(): Observable<BackofficeCartridgeGroups[]> {
    return this.http.get<BackofficeCartridgeGroups[]>(`${this.cartridgeServiceApiUrl}?scope=overview`);
  }

  updateVariant(
    variant: BackofficeCartridgeUpdateVariant,
    serviceId: string,
    serviceVersion: number
  ): Observable<BackofficeCartridge> {
    return this.http.post<BackofficeCartridge>(`${this.cartridgeServiceApiUrl}/${serviceId}/${variant.variantId}`, {
      ...variant,
      version: serviceVersion,
    });
  }

  updateVariantState(
    variant: BackofficeCartridgeUpdateVariant,
    serviceId: string,
    serviceVersion: number
  ): Observable<BackofficeCartridge> {
    return this.http.post<BackofficeCartridge>(
      `${this.cartridgeServiceApiUrl}/${serviceId}/${variant.variantId}/status`,
      {
        version: serviceVersion,
        status: variant.variantStatus,
      }
    );
  }
}
